import clsx from 'clsx';
import { memo, useMemo, useState } from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomButton,
    Badge,
    Collection,
    HeadlessTree,
    HeadlessTreeItem,
    HeadlessTreeItemContent,
    type HeadlessTreeProps,
    type Key,
    renderIcon,
    Tooltip,
    treeDropIndicatorClassName,
} from '@gitbook/spine-react';

import {
    type AnySiteItem,
    flattenGroupedSections,
    flattenSiteStructure,
    getIndexInSiteStructure,
    getSiteTreeItem,
    type SiteCapability,
    type UseUpgradeSite,
} from '../../../state';
import { SiteItemAdaptiveContentButton } from './SiteItemAdaptiveContentButton';
import {
    SiteSectionActionsButton,
    type SiteSectionActionsButtonProps,
} from './SiteSectionActionsButton';
import {
    SiteSectionGroupActionsButton,
    type SiteSectionGroupActionsButtonProps,
} from './SiteSectionGroupActionsButton';
import {
    SiteVariantActionsButton,
    type SiteVariantActionsButtonProps,
} from './SiteVariantActionsButton';
import { SiteVariantLanguageButton } from './SiteVariantLanguageButton';
import {
    type UseSiteStructureTreeDragAndDropProps,
    useSiteStructureTreeDragAndDrop,
} from './useSiteStructureTreeDragAndDrop';

export interface SiteStructureTreeProps
    extends Pick<HeadlessTreeProps<AnySiteItem>, 'selectedKeys' | 'onSelectionChange'>,
        Pick<UseSiteStructureTreeDragAndDropProps, 'onReorder'>,
        Pick<SiteStructureTreeItemContentProps, 'onCreateVariant'> {
    siteStructure: api.SiteStructure;
    site: api.Site;
    orgAllContent: api.OrganizationAllContent;
    organization: api.Organization;
    sitePermissions: SiteCapability;
    className?: string;
    isDisabled?: boolean;
    expandedKeys: Set<Key>;
    setExpandedKeys: (keys: Set<Key>) => void;
    upgrade: UseUpgradeSite | null;
}

export function SiteStructureTree(props: SiteStructureTreeProps) {
    const {
        siteStructure,
        site,
        organization,
        sitePermissions,
        selectedKeys,
        onSelectionChange,
        orgAllContent,
        className,
        onReorder,
        isDisabled,
        upgrade,
        expandedKeys,
        setExpandedKeys,
    } = props;

    const siteItem = useMemo(() => {
        const siteSections =
            siteStructure.type === 'sections'
                ? flattenGroupedSections(siteStructure.structure)
                : [];
        const siteSectionsWithVariants = siteSections.filter(
            (siteSection) => siteSection.siteSpaces.length > 1
        );
        return getSiteTreeItem(
            { site, structure: siteStructure },
            {
                expandableSiteSectionIds: [
                    ...siteSectionsWithVariants.map((siteSection) => siteSection.id),
                ],
            }
        );
    }, [site, siteStructure]);

    const siteItems = useMemo(() => flattenSiteStructure(siteStructure), [siteStructure]);

    const [draggedKeys, setDraggedKeys] = useState<Set<Key>>(new Set());

    const { dragAndDropHooks } = useSiteStructureTreeDragAndDrop({
        draggedKeys,
        setDraggedKeys,
        expandedKeys,
        setExpandedKeys,
        getItemIndex: (key) => getIndexInSiteStructure(siteStructure, String(key)),
        getItem: (key) => siteItems.find((item) => item.id === key) ?? null,
        organization,
        site,
        checkIsSectionInGroup: (siteSection) =>
            Boolean(siteStructure.type === 'sections' && siteSection.sectionGroup),
        onReorder,
        isDisabled: !sitePermissions.canAdminSite,
    });

    return (
        <HeadlessTree<AnySiteItem>
            // Temporary workaround for a bug when switching between sites
            key={siteItem.id}
            items={siteItem.children}
            aria-label="Site structure"
            className={clsx(treeDropIndicatorClassName, className)}
            expandedKeys={expandedKeys}
            onExpandedChange={setExpandedKeys}
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
            selectionBehavior="replace"
            dragAndDropHooks={dragAndDropHooks}
            disabledKeys={
                isDisabled && draggedKeys.size === 0 ? siteItems.map((item) => item.id) : []
            }
        >
            {function renderItem(treeItem) {
                const isExpandable = 'children' in treeItem && treeItem.children.length > 0;

                return (
                    <HeadlessTreeItem
                        textValue={treeItem.title}
                        className={clsx(
                            'group my-px cursor-pointer rounded pl-[calc((var(--tree-item-level)-1)*1rem)] hover:bg-side',
                            // Disabled
                            'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-disabled',
                            // Selected
                            'data-[selected]:bg-[--sp-color-primary-100] dark:data-[selected]:bg-[--sp-color-primary-800]',
                            // Dragging
                            'data-[dragging]:opacity-drag-preview',
                            // Drop target
                            'data-[drop-target]:outline-dashed data-[drop-target]:outline-1 data-[drop-target]:outline-primary'
                        )}
                    >
                        <SiteStructureTreeItemContent
                            value={treeItem}
                            isExpandable={isExpandable}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            siteStructure={siteStructure}
                            orgAllContent={orgAllContent}
                            upgrade={upgrade}
                        />
                        {'children' in treeItem ? (
                            <Collection items={treeItem.children}>{renderItem}</Collection>
                        ) : null}
                    </HeadlessTreeItem>
                );
            }}
        </HeadlessTree>
    );
}

interface SiteStructureTreeItemContentProps
    extends Omit<SiteSectionActionsButtonProps, 'siteSection'>,
        Omit<SiteSectionGroupActionsButtonProps, 'siteSectionGroup'>,
        Omit<SiteVariantActionsButtonProps, 'siteSpace'> {
    value: AnySiteItem;
    isExpandable: boolean;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
}

const SiteStructureTreeItemContent = memo(function SiteStructureTreeItemContent(
    props: SiteStructureTreeItemContentProps
) {
    const {
        value,
        isExpandable,
        site,
        sitePermissions,
        siteStructure,
        orgAllContent,
        organization,
        upgrade,
        onCreateVariant,
    } = props;
    const isDefault = Boolean(
        (value.type === 'site-section' && value.siteSection.default) ||
            (value.type === 'site-space' && value.siteSpace.default)
    );
    const isHidden = Boolean(
        (value.type === 'site-space' && value.siteSpace.hidden) ||
            (value.type === 'site-section' &&
                value.siteSection.siteSpaces.length > 0 &&
                value.siteSection.siteSpaces.every((space) => space.hidden))
    );
    return (
        <HeadlessTreeItemContent>
            <div className="flex items-center gap-2 px-2 py-1.5">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                    {isExpandable ? (
                        <AtomButton
                            slot="chevron"
                            className="flex items-center justify-center rounded p-1 focus-visible:shadow-focus-ring-inset"
                        >
                            <Icon.ChevronRight className="size-3 transition-transform group-aria-expanded:rotate-90" />
                        </AtomButton>
                    ) : (
                        <span className="w-5" />
                    )}
                    {renderIcon(value.icon, { className: 'size-4 shrink-0 mt-0.5 text-muted' })}
                    <div className="flex min-w-0 flex-1 items-center gap-2 truncate">
                        {value.title}
                        {value.type === 'site-section-group' ? (
                            <Badge label="Group" size="xsmall" kind="secondary" />
                        ) : null}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    {isHidden ? (
                        <Tooltip label="Hidden from site navigation">
                            <div className="flex shrink-0">
                                <Icon.EyeOff className="size-3 text-muted" />
                            </div>
                        </Tooltip>
                    ) : null}
                    {isDefault ? (
                        <Tooltip
                            label={
                                value.type === 'site-section' || siteStructure.type === 'siteSpaces'
                                    ? 'Home of the site'
                                    : 'Home of the section'
                            }
                        >
                            <div className="flex shrink-0">
                                <Icon.Home className="size-3 text-muted" />
                            </div>
                        </Tooltip>
                    ) : null}
                    {value.type === 'site-section' && value.siteSection.condition ? (
                        <SiteItemAdaptiveContentButton
                            item={value.siteSection}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            size="small"
                        />
                    ) : null}
                    {value.type === 'site-space' && value.siteSpace.condition ? (
                        <SiteItemAdaptiveContentButton
                            item={value.siteSpace}
                            organization={organization}
                            site={site}
                            sitePermissions={sitePermissions}
                            size="small"
                        />
                    ) : null}
                    {value.type === 'site-space' && value.space.language ? (
                        <SiteVariantLanguageButton
                            siteStructure={siteStructure}
                            siteSpace={value.siteSpace}
                        />
                    ) : null}
                    <div className="flex shrink-0 opacity-0 empty:hidden group-hover:opacity-100 has-[[aria-expanded=true]]:opacity-100">
                        {value.type === 'site-section-group' ? (
                            <SiteSectionGroupActionsButton
                                siteSectionGroup={value.siteSectionGroup}
                                organization={organization}
                                site={site}
                                sitePermissions={sitePermissions}
                                size="small"
                                siteStructure={siteStructure}
                                upgrade={upgrade}
                            />
                        ) : value.type === 'site-section' ? (
                            <SiteSectionActionsButton
                                orgAllContent={orgAllContent}
                                siteStructure={siteStructure}
                                siteSection={value.siteSection}
                                organization={organization}
                                site={site}
                                sitePermissions={sitePermissions}
                                size="small"
                                upgrade={upgrade}
                                onCreateVariant={onCreateVariant}
                            />
                        ) : value.type === 'site-space' ? (
                            <SiteVariantActionsButton
                                siteSpace={value.siteSpace}
                                organization={organization}
                                site={site}
                                siteStructure={siteStructure}
                                sitePermissions={sitePermissions}
                                size="small"
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </HeadlessTreeItemContent>
    );
});
